import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTable, DataTablePageEvent, DataTableSortEvent, SortOrder } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Toast } from 'primereact/toast'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { downloadFileFromURL } from '../Functions/downloadFile'
import extractDateFromFilename from '../Functions/extractDateFromFilename'
import getVisitTypeFromFilename from '../Functions/getVisitTypeFromFilename'
import useMemberGcsFiles, { GcsFile } from '../Hooks/useMemberGcsFiles'
import { Member } from '../Hooks/useMembers'
import { LazyState } from '../Hooks/useWbapiInstance'
import DownloadButton from './DownloadButton'
import ViewButton from './ViewButton'

interface Props {
  member: Member | undefined
}

export default function MemberDocs({ member }: Props) {
  const [viewing, setViewing] = useState<{ url: string; mime: string } | undefined>()

  const inferMimeFromName = (name: string): string => {
    const lower = name.toLowerCase()
    if (lower.endsWith('.pdf')) return 'application/pdf'
    if (lower.endsWith('.png')) return 'image/png'
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg'
    if (lower.endsWith('.gif')) return 'image/gif'
    if (lower.endsWith('.webp')) return 'image/webp'
    return ''
  }
  const { data, isLoading, getSignedUrl } = useMemberGcsFiles(member?.aaneelId)
  const toast = useRef<Toast>(null)
  const navigate = useNavigate()
  const [filesFormatted, setFilesFormatted] = useState<GcsFile[]>([])

  const header = () => {
    return (
      <div className="flex justify-content-between align-items-center">
        <h2>{member?.fullName}'s Documents</h2>
        <div>
          <Button
            type="button"
            className="mx-2"
            style={{ maxHeight: '5vh' }}
            size="small"
            icon="pi pi-chevron-left"
            label="Return to search"
            onClick={() => navigate('/search')}
          />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (data) {
      const formatted = data
        .filter((x) => !x.name.endsWith('.xml')) //filter xmls for now
        .map((file) => {
          const timeCreated = extractDateFromFilename(file.name, file.timeCreated)
          const visitType = getVisitTypeFromFilename(file.name)

          //instead of putting the word "CCD" on the screen/button etc... Use the words "Chart Summary (CCD)" so that the end user better understands what it is
          const category =
            file.name.endsWith('.pdf')
              ? 'Progress Note'
              : file.metadata.category == 'CCD'
                ? 'Chart Summary (CCD)'
                : file.metadata.category

          return {
            ...file,
            timeCreated,
            visitType,
            metadata: { ...file.metadata, category },
          }
        })
      setFilesFormatted(formatted)
    }
  }, [data])

  const [state, setState] = useState<LazyState>({
    first: 0,
    rows: 10,
    page: 1,
    sortField: 'timeCreated',
    sortOrder: -1 as SortOrder,
    filters: {},
  })

  if (!member) return <></>

  const getSubstringAfterLastSlash = (inputString: string) => {
    const lastIndex = inputString.lastIndexOf('/')
    if (lastIndex !== -1) {
      return inputString.substring(lastIndex + 1)
    }
    // Handle the case when there's no slash in the string
    return inputString
  }

  const handleLookup = (rowData: GcsFile, view: boolean) => {
    getSignedUrl.mutate(rowData.name, {
      async onSuccess(data) {
        if (view) {
          const resp = await fetch(data)
          const blob = await resp.blob()
          const mime = blob.type || inferMimeFromName(rowData.name)
          const previewable = mime === 'application/pdf' || mime.startsWith('image/')
          if (previewable) {
            setViewing({ url: URL.createObjectURL(blob), mime })
          } else {
            downloadFileFromURL(data, rowData.name)
          }
        } else {
          downloadFileFromURL(data, rowData.name)
        }
      },
    })
  }

  const onPage = (event: DataTablePageEvent) => setState({ ...state, ...event })

  const onSort = (event: DataTableSortEvent) => setState({ ...state, ...event })

  return (
    <Card>
      <DataTable
        loading={isLoading}
        size="small"
        header={header}
        value={filesFormatted}
        paginator
        sortField={state.sortField}
        sortOrder={state.sortOrder}
        onPage={onPage}
        onSort={onSort}
        stripedRows
        first={state.first}
        rows={state.rows}
        totalRecords={filesFormatted?.length ?? 0}
        emptyMessage="No files found."
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
      >
        <Column field="name" sortable header="Name" body={(rowData: GcsFile) => getSubstringAfterLastSlash(rowData.name)} />
        <Column
          align={'center'}
          field="timeCreated"
          sortable
          dataType="date"
          header="Date"
          body={(rowData: GcsFile) =>
            rowData.timeCreated == undefined ? 'Unknown' : rowData.timeCreated.toLocaleDateString('en-US', { timeZone: 'EST' })
          }
        />
        <Column
          align={'center'}
          field="visitType"
          header="Visit Type"
          sortable
          body={(rowData: GcsFile) => getVisitTypeFromFilename(rowData.name)}
        />
        <Column align={'center'} field="metadata.category" sortable header="Type" />
        <Column
          // header="View"
          align={'center'}
          body={(rowData: GcsFile) =>
            rowData.name.endsWith('xml') ? (
              <DownloadButton disabled={getSignedUrl.isPending} onClick={() => handleLookup(rowData, false)} />
            ) : (
              <ViewButton disabled={getSignedUrl.isPending} onClick={() => handleLookup(rowData, true)} />
            )
          }
        />
      </DataTable>
      <Dialog
        visible={!!viewing}
        modal
        maximizable
        className="iframe-container"
        style={{ width: '80vw', height: '70vh' }}
        breakpoints={{ '960px': '100vw' }}
        //header={headerElement}
        //footer={footerContent}
        //style={{ width: '50rem' }}
        onHide={() => {
          if (!viewing) return
          URL.revokeObjectURL(viewing.url)
          setViewing(undefined)
        }}
      >
        {viewing?.mime === 'application/pdf' ? (
          <embed src={viewing.url} type="application/pdf" style={{ width: '100%', height: '98%' }} />
        ) : viewing?.mime.startsWith('image/') ? (
          <img src={viewing.url} alt="document preview" style={{ maxWidth: '100%', maxHeight: '98%', objectFit: 'contain' }} />
        ) : null}
      </Dialog>
      <Toast ref={toast} />
    </Card>
  )
}
